import { useEffect, useMemo } from 'react';
import _ from 'lodash';
import { t } from '@lingui/macro';

import { addPlmPrefix, usePlmId } from '../utilities/PgmUtilities';
import { useRequestState, useSingletonValue } from '../utilities/Hooks';
import { DEFAULT_VALIDATION_ERROR_TEXT } from '../components/form/FormConstants';
import useAlerts from '../stores/AlertsStore';
import { getLogger } from '../utilities/UseLog';
import { useUserGroups } from '../stores/SessionStore';
import {
    cleanUrl,
    GLOBAL_RESPONSE_INTERCEPTORS,
    handleResponse,
    handleResponseError,
    sendRequest,
} from './ApiRequestService';

const log = getLogger('DataService');

// Like query parameter like VMware Infrastructure: subType, Monitored Infrastructure: deviceType, Interfaces: ifType
// subType=hypervisor,virtualmachine,vcenter
// query={"subType":{"$in":["hypervisor","virtualmachine","vcenter"]}}

const ADMIN_TYPE_FILTER_STATE_QUERY = {
    deviceType: '/admin/monitoredDeviceConfig',
    subType: '/admin/virtualInfrastructureConfig',
    ifType: '/admin/interfaces',
};
const ADMIN_TYPE_FILTER_STATE_QUERY_KEYS = Object.keys(ADMIN_TYPE_FILTER_STATE_QUERY);
const QUERY_KEY = 'query';
const MONITORED_RESCAN_URL = '/ngp/v2/devices/reclassify';

export const contentTypeHeader = 'Content-type';
export const contentTypeJSON = 'application/json';
export const utf8 = 'charset=UTF-8';
export const contentTypePlainText = 'text/plain';

export const HTTP_STATUS_200 = 200;
export const HTTP_STATUS_400 = 400;
export const HTTP_STATUS_401 = 401;
export const HTTP_STATUS_403 = 403;
export const HTTP_STATUS_500 = 500;

class HttpError extends Error {
    constructor(response, responseBody) {
        super(response.statusText);
        this.response = response;
        this.responseBody = responseBody;
    }

    getStatus() {
        return this.response.status;
    }
}

function getQueryValue(key, value) {
    const valuesArray = value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    const queryObj = {
        [key]: { $in: valuesArray },
    };
    return JSON.stringify(queryObj);
}

export function throwValidationError(errorText) {
    throw new HttpError(
        {
            status: HTTP_STATUS_400,
        },
        errorText || DEFAULT_VALIDATION_ERROR_TEXT
    );
}

function addGlobalResponseInterceptor(key, callback) {
    GLOBAL_RESPONSE_INTERCEPTORS[key] = callback;
}

function removeGlobalResponseInterceptor(key) {
    GLOBAL_RESPONSE_INTERCEPTORS[key] = undefined;
}

export function useGlobalResponseInterceptor(key, callback) {
    useEffect(() => {
        addGlobalResponseInterceptor(key, callback);

        return () => removeGlobalResponseInterceptor(key);
    });
}

/**
 * @typedef {{ [index: string]: string | number | boolean | Date | null }} Dictionary
 * @typedef {Dictionary} QueryParams
 * @typedef {Partial<RequestInit>} ExtraOptions
 * @typedef {(error: Error) => any | Promise<any>} ErrorHandler
 */

/**
 * @template D
 * @typedef {(url: string, queryParams?: QueryParams, extraOptions?: ExtraOptions, errorHandler?: ErrorHandler) => Promise<D>} ApiRequest
 */

/**
 * @template D, B
 * @typedef {(url: string, body: B, queryParams?: QueryParams, extraOptions?: ExtraOptions, errorHandler?: ErrorHandler) => Promise<{rows: Array<D>, length: number}>} ApiRequestWithBody
 */

/**
 * @template D, B
 * @typedef DataServiceInstance
 * @property {string?} plmId
 * @property {ApiRequest<D>} get<D>
 * @property {ApiRequest<{ rows: Array<D>, length: number }>} getList<D>
 * @property {ApiRequestWithBody<D, B>} post<D, B>
 * @property {ApiRequestWithBody<D, FormData} postFormData<B>
 * @property {ApiRequestWithBody<D, B>} put<D, B>
 * @property {ApiRequestWithBody<D, B>} patch<D, B>
 * @property {ApiRequestWithBody<D, B>} remove<D, B>
 */

/**
 * @template D, B
 * Generic service to interact with an API.  Right now we
 *   are wrapping fetch, but if that changes, we should be able to
 *   just make the changes here, and everything else will be unaffected
 * @returns {DataServiceInstance<D, B>}
 */
function DataServiceFactory(plmId, userGroups) {
    const GET = 'GET';
    const POST = 'POST';
    const PUT = 'PUT';
    const PATCH = 'PATCH';
    const DELETE = 'DELETE';
    const DEFAULT_GROUP_UUID = '00000000-0000-0000-0000-000000000000';

    const isAdminTypeFilterStateQuery = (value, key, url) => {
        if (_.isEmpty(value)) {
            return false;
        }

        if (!ADMIN_TYPE_FILTER_STATE_QUERY_KEYS.includes(key)) {
            return false;
        }

        if (url.includes(ADMIN_TYPE_FILTER_STATE_QUERY[key])) {
            return true;
        }

        // devices/reclassify, not start with /admin
        // If there are some special logic for isAdminTypeFilterStateQuery, please add here

        return MONITORED_RESCAN_URL === url;
    };

    /**
     * Take the params object and turn it into a URL querystring
     * @param {Object} queryParams
     * @param {string} url
     * @returns {string}
     */
    function paramsToQueryString(queryParams = {}, url) {
        if (_.isEmpty(queryParams)) return '';

        const params = new URLSearchParams();

        _.forEach(queryParams, (value, key) => {
            if (_.isArray(value)) {
                value.forEach((v) => params.append(key, v));
            } else if (isAdminTypeFilterStateQuery(value, key, url)) {
                params.append(QUERY_KEY, getQueryValue(key, value));
            } else {
                params.append(key, value);
            }
        });

        return `?${params.toString()}`;
    }

    /**
     * Take in the URL and the query params and build out a URL
     * @param {String} url
     * @param {Object} queryParams
     * @returns {string}
     */
    function buildUrl(url, queryParams) {
        if (Array.isArray(userGroups)) {
            return cleanUrl(
                `${url}${paramsToQueryString(
                    {
                        ...queryParams,
                        userGroups,
                    },
                    url
                )}`
            );
        }

        // If plmId is not null or undefined, we're operating from a PGM, allow the PGM user to view all data
        // GROUPS_TODO: update PGMs to constrain data access based on group membership
        if (!_.isNil(plmId)) {
            return cleanUrl(
                `${url}${paramsToQueryString(
                    {
                        ...queryParams,
                        userGroups: [DEFAULT_GROUP_UUID],
                    },
                    url
                )}`
            );
        }

        return cleanUrl(`${url}${paramsToQueryString(queryParams, url)}`);
    }

    /**
     * Build out options specific to the 3rd party we are using
     * @param {String} method
     * @param {Object} extraOptions
     * @returns {Object}
     */
    function buildRequestOptions(method, extraOptions) {
        return _.extend({}, { method }, extraOptions);
    }

    function callAPIWithBody(url, queryParams, extraOptions, errorHandler = handleResponseError, which, body) {
        return callAPI(
            url,
            queryParams,
            _.extend({}, extraOptions, {
                headers: {
                    [contentTypeHeader]: `${contentTypeJSON}; ${utf8}`,
                },
                body: JSON.stringify(body),
            }),
            errorHandler,
            which
        );
    }

    function callAPI(url, queryParams, extraOptions, errorHandler = handleResponseError, which) {
        return sendRequest(
            url,
            addPlmPrefix(buildUrl(url, queryParams), plmId),
            buildRequestOptions(which, extraOptions),
            errorHandler
        );
    }

    function get(url, queryParams, extraOptions, errorHandler) {
        return callAPI(url, queryParams, extraOptions, errorHandler, GET);
    }

    function post(url, body, queryParams, extraOptions, errorHandler) {
        return callAPIWithBody(url, queryParams, extraOptions, errorHandler, POST, body);
    }

    function put(url, body, queryParams, extraOptions, errorHandler) {
        return callAPIWithBody(url, queryParams, extraOptions, errorHandler, PUT, body);
    }

    function patch(url, body, queryParams, extraOptions, errorHandler) {
        return callAPIWithBody(url, queryParams, extraOptions, errorHandler, PATCH, body);
    }

    function remove(url, body, queryParams, extraOptions, errorHandler) {
        return callAPIWithBody(url, queryParams, extraOptions, errorHandler, DELETE, body);
    }

    function getList(url, queryParams, extraOptions) {
        return (params) => {
            return callAPI(
                url,
                {
                    ...params,
                    ...queryParams,
                },
                extraOptions,
                GET
            ).then(convertListResponse);
        };
    }

    function postFormData(url, formDataObject, queryParams, extraOptions, errorHandler = handleResponseError) {
        // eslint-disable-next-line react-portal/no-global-fetch
        return fetch(
            addPlmPrefix(buildUrl(url, queryParams), plmId),
            _.extend({}, extraOptions, {
                method: POST,
                body: formDataObject,
            })
        )
            .then(handleResponse)
            .catch(errorHandler);
    }

    function convertListResponse(response) {
        return {
            rows: response.data,
            length: response.count,
        };
    }

    return {
        plmId,
        get,
        getList,
        post,
        postFormData,
        put,
        patch,
        remove,
    };
}

function buildParams(dataSource, params) {
    return _.merge({}, dataSource?.params || {}, params);
}

export function useDataService() {
    const plmId = usePlmId();
    const [userGroups] = useUserGroups();

    return useMemo(() => DataServiceFactory(plmId, userGroups), [plmId, userGroups]);
}

export function useGlobalDataService() {
    return useSingletonValue(() => DataServiceFactory());
}

export function useDataUrl(url, params) {
    const { data, isLoading, error, methods } = useRequestState();
    const dataService = useDataService();

    useEffect(() => {
        doGet();

        async function doGet() {
            try {
                methods.onInit();
                methods.onLoad(await dataService.get(url, params));
            } catch (e) {
                methods.onError(e);
            }
        }
    }, [url, params, dataService, methods]);

    return [data, isLoading, error];
}

/**
 * @param {import('./PanelDataService').DataSource} dataSource
 * @param {object} params
 * @returns {[{data: any}, boolean, Error?]}
 */
export function useDataSource(dataSource, params) {
    const {
        data,
        isLoading,
        error,
        methods: { onInit, onLoad, onError },
    } = useRequestState();
    const dataService = useDataService();
    const [, /* Not used */ alertsActions] = useAlerts();

    useEffect(() => {
        if (dataSource === undefined || _.isEmpty(params)) {
            return;
        }

        doLoad();

        async function doLoad() {
            const requestParams = buildParams(dataSource, params);
            onInit();

            try {
                onLoad(await dataService.get(dataSource.url, requestParams));
            } catch (err) {
                onError(err);
            }
        }
    }, [dataService, dataSource, onError, onInit, onLoad, params]);

    useEffect(() => {
        if (error) {
            log.error(error);

            if (error?.response?.status === HTTP_STATUS_403) {
                alertsActions.createErrorAlert(
                    t`You do not have access to this resource.  Please contact your system administrator for access.`
                );
            } else {
                alertsActions.createErrorAlert(
                    t`An error occurred on the server.  If this issue persists, please contact support.`
                );
            }
        }
    }, [error, alertsActions]);

    return [data, isLoading, error];
}

export { DataServiceFactory };
